import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { authed } from '../lib/api';
import { formatMoney } from '../lib/utils';
import { OrderStatusBadge, PaymentStatusBadge, isOverdueCollection, formatOverdueAge } from '../components/StatusBadges';

// Distributor-facing progress tracker — only 4 forward stages. Cancelled
// orders are handled separately below (no tracker, just the cancellation
// message). Legacy packed/shipped orders are backfilled by the super-admin
// maintenance endpoint, so they never reach this UI.
const STAGES = ['placed', 'confirmed', 'dispatched', 'delivered'];
const STAGE_LABELS = { placed: 'Placed', confirmed: 'Confirmed', dispatched: 'Dispatched', delivered: 'Delivered' };

const CANCELLED_MESSAGE = 'This Order has been Cancelled as requested by you and Confirmed by SRI DURGA FOODS.';

export default function OrderDetail() {
    const { id } = useParams();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        (async () => {
            try {
                const r = await authed('distributor', { method: 'GET', url: `/orders/${id}` });
                setOrder(r.data);
            } catch (e) { console.error(e); }
            setLoading(false);
        })();
    }, [id]);
    if (loading) return <div className="min-h-screen flex items-center justify-center bg-sdf-ivory"><div className="spinner"/></div>;
    if (!order) return <div className="min-h-screen bg-sdf-ivory"><Header /><main className="max-w-3xl mx-auto p-6"><div className="card-soft p-6">Order not found. <Link to="/orders" className="text-sdf-red font-bold">Back to Orders</Link></div></main></div>;
    const stageIdx = STAGES.indexOf(order.status);
    return (
        <div className="min-h-screen bg-sdf-ivory">
            <Header />
            <main className="max-w-3xl mx-auto px-4 py-6 space-y-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-extrabold">#{order.order_number}</h1>
                        <div className="text-xs text-sdf-text-2">{new Date(order.created_at).toLocaleString()}</div>
                    </div>
                    <div className="text-right">
                        <div className="font-extrabold text-lg">{formatMoney(order.total)}</div>
                        <div className="text-[10px] font-bold uppercase tracking-widest">{order.payment_method === 'credit' ? 'CREDIT PURCHASE' : 'CASH ON DELIVERY'}</div>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2" data-testid="order-detail-badges">
                    <OrderStatusBadge status={order.status} testid="order-detail-status-badge" />
                    <PaymentStatusBadge status={order.payment_status} testid="order-detail-payment-badge" />
                    {isOverdueCollection(order) && (() => {
                        const age = formatOverdueAge(order);
                        return (
                            <span
                                className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded-full border bg-amber-100 text-amber-900 border-amber-300 whitespace-nowrap"
                                data-testid="order-detail-payment-due"
                            >
                                Payment Due{age ? ` · ${age}` : ''}
                            </span>
                        );
                    })()}
                </div>
                {order.status === 'cancelled' ? (
                    <div className="card-soft p-4 border border-red-200 bg-red-50" data-testid="order-cancelled-banner">
                        <div className="text-red-700 font-bold text-xs tracking-widest">ORDER CANCELLED</div>
                        <p className="mt-2 text-sm text-red-800 leading-6">{CANCELLED_MESSAGE}</p>
                    </div>
                ) : (
                    <div className="card-soft p-4">
                        <div className="text-sdf-red font-bold text-xs tracking-widest">TRACKING</div>
                        <div className="grid grid-cols-4 gap-2 mt-3">
                            {STAGES.map((s, i) => (
                                <div key={s} className="text-center">
                                    <div className={`h-2 rounded-full ${i <= stageIdx ? 'bg-sdf-red' : 'bg-neutral-200'}`} />
                                    <div className={`mt-1 text-[10px] font-bold uppercase tracking-widest ${i <= stageIdx ? 'text-sdf-text' : 'text-sdf-text-2'}`}>{STAGE_LABELS[s]}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                <div className="card-soft p-4">
                    <div className="text-sdf-red font-bold text-xs tracking-widest">DELIVERY ADDRESS</div>
                    <div className="text-sm text-sdf-text mt-2">{order.address.full_name} · +91 {order.address.phone}</div>
                    <div className="text-sm text-sdf-text-2">{order.address.line1}{order.address.line2 ? `, ${order.address.line2}` : ''}</div>
                    <div className="text-sm text-sdf-text-2">{order.address.city}, {order.address.state} - {order.address.pin_code}</div>
                </div>
                <div className="card-soft p-4">
                    <div className="text-sdf-red font-bold text-xs tracking-widest">ITEMS</div>
                    <div className="mt-2 divide-y divide-sdf-line">
                        {order.items.map((it, idx) => (
                            <div key={idx} className="py-2 flex items-center justify-between text-sm">
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold line-clamp-1">{it.flavour_name}</div>
                                    <div className="text-xs text-sdf-text-2">{it.size_label} × {it.quantity}</div>
                                </div>
                                <div className="font-bold">{formatMoney(it.price * it.quantity)}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
}
